import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import ConfirmPopup from '../components/ConfirmPopup';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './OrdersPage.css';
import { formatSizeSpaced } from '../utils/formatSize';

export default function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [orderToComplete, setOrderToComplete] = useState(null);
  const [expandedDeliveryDetails, setExpandedDeliveryDetails] = useState({});

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/v1/orders');
        setOrders(response.data.orders || []);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const handleCancelClick = (order) => {
    setOrderToCancel(order);
    setShowCancelPopup(true);
  };

  const confirmCancellation = async () => {
    if (!orderToCancel) return;

    try {
      await axios.patch(`/api/v1/orders/${orderToCancel.id}`, { status: 'cancelled' });
      // Update the order status to 'cancelled' with cancelled_at timestamp
      const now = new Date().toISOString();
      setOrders(orders.map(order => 
        order.id === orderToCancel.id 
          ? { ...order, status: 'cancelled', cancelled_at: now }
          : order
      ));
      setShowCancelPopup(false);
      setOrderToCancel(null);
    } catch (error) {
      console.error('Error canceling order:', error);
      alert(error.response?.data?.error || 'Помилка при скасуванні замовлення');
    }
  };

  const cancelCancellation = () => {
    setShowCancelPopup(false);
    setOrderToCancel(null);
  };

  const handleCompleteClick = (order) => {
    setOrderToComplete(order);
    setShowCompletePopup(true);
  };

  const confirmCompletion = async () => {
    if (!orderToComplete) return;

    try {
      await axios.patch(`/api/v1/orders/${orderToComplete.id}`, { status: 'completed' });
      // Update the order status to 'completed' with delivered_at timestamp
      const now = new Date().toISOString();
      setOrders(orders.map(o => 
        o.id === orderToComplete.id 
          ? { ...o, status: 'completed', delivered_at: now }
          : o
      ));
      setShowCompletePopup(false);
      setOrderToComplete(null);
    } catch (error) {
      console.error('Error completing order:', error);
      alert(error.response?.data?.error || 'Помилка при завершенні замовлення');
    }
  };

  const cancelCompletion = () => {
    setShowCompletePopup(false);
    setOrderToComplete(null);
  };

  const handleCancelOrderAsSeller = async (order) => {
    try {
      await axios.patch(`/api/v1/orders/${order.id}`, { status: 'cancelled' });
      // Update the order status to 'cancelled' with cancelled_at timestamp
      const now = new Date().toISOString();
      setOrders(orders.map(o => 
        o.id === order.id 
          ? { ...o, status: 'cancelled', cancelled_at: now }
          : o
      ));
    } catch (error) {
      console.error('Error canceling order:', error);
      alert(error.response?.data?.error || 'Помилка при скасуванні замовлення');
    }
  };

  const handleReorder = async (order) => {
    if (!order.line_items || order.line_items.length === 0) {
      alert('Немає товарів у замовленні');
      return;
    }

    try {
      // Prepare items for bulk add
      const itemsToAdd = [];
      
      for (const item of order.line_items) {
        // Use product_variation_id if available, otherwise try to get it from product
        if (item.product_variation_id) {
          itemsToAdd.push({
            product_variation_id: item.product_variation_id,
            quantity: item.quantity
          });
        } else if (item.product_id) {
          // Fallback: fetch product to get variation
          try {
            const productResponse = await axios.get(`/api/v1/products/${item.product_id}`);
            const product = productResponse.data;
            
            if (product.product_variations && product.product_variations.length > 0) {
              // Use main variation or first available
              const variation = product.product_variations.find(v => v.main) || product.product_variations[0];
              
              itemsToAdd.push({
                product_variation_id: variation.id,
                quantity: item.quantity
              });
            }
          } catch (error) {
            console.error(`Error fetching product ${item.product_id}:`, error);
          }
        }
      }

      // Add all items to cart in one request
      if (itemsToAdd.length > 0) {
        await axios.post('/api/v1/cart/add_multiple', {
          items: itemsToAdd
        });
      }
      
      // Dispatch cart updated event
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      
      // Navigate to cart
      navigate('/cart');
    } catch (error) {
      console.error('Error reordering:', error);
      alert(error.response?.data?.error || 'Помилка при додаванні товарів до кошика');
    }
  };

  if (loading) {
    return (
      <div className="orders-page">
        <div className="loading-indicator">
          <div className="loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      {orders.length === 0 ? (
        <div className="orders-empty">
          <p>У вас немає замовлень</p>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="order-item">
              <div className="order-item-header">
                <div className="order-header-left">
                  <span className="order-number">Замовлення #{order.id}</span>
                </div>
                <span className="order-date">
                  {order.created_at ? (() => {
                    const date = new Date(order.created_at);
                    const dateStr = date.toLocaleDateString('uk-UA');
                    const timeStr = date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
                    return `${dateStr} ${timeStr}`;
                  })() : ''}
                </span>
              </div>
              <div className="order-item-body">
                <div className="order-account-info">
                  {order.is_buyer ? (
                    <span className="order-account-text">
                      Продавець:{' '}
                      {order.seller_account_name || order.seller_account_id ? (
                        <span 
                          className="order-account-link"
                          onClick={() => {
                            if (order.seller_account_name) {
                              const accountName = order.seller_account_name.startsWith('@') 
                                ? order.seller_account_name 
                                : `@${order.seller_account_name}`;
                              navigate(`/${accountName}`);
                            } else if (order.seller_account_id) {
                              navigate(`/account/${order.seller_account_id}`);
                            }
                          }}
                        >
                          {order.seller_account_name || `Account ${order.seller_account_id}`}
                        </span>
                      ) : (
                        'Не вказано'
                      )}
                    </span>
                  ) : (
                    <>
                      <div className="order-account-text-wrapper">
                        <span className="order-account-text">
                          Покупець:{' '}
                          {order.buyer_account_name || order.buy_account_id ? (
                            <span 
                              className="order-account-link"
                              onClick={() => {
                                if (order.buyer_account_name) {
                                  const accountName = order.buyer_account_name.startsWith('@') 
                                    ? order.buyer_account_name 
                                    : `@${order.buyer_account_name}`;
                                  navigate(`/${accountName}`);
                                } else if (order.buy_account_id) {
                                  navigate(`/account/${order.buy_account_id}`);
                                }
                              }}
                            >
                              {order.buyer_account_name || `Account ${order.buy_account_id}`}
                            </span>
                          ) : (
                            'Не вказано'
                          )}
                        </span>
                        {order.is_seller && order.delivery_details && (
                          <button
                            className="order-delivery-expand-btn"
                            onClick={() => {
                              setExpandedDeliveryDetails(prev => ({
                                ...prev,
                                [order.id]: !prev[order.id]
                              }));
                            }}
                          >
                            {expandedDeliveryDetails[order.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </button>
                        )}
                      </div>
                      {order.is_seller && order.delivery_details && expandedDeliveryDetails[order.id] && (
                        <div className="order-delivery-details">
                          {order.delivery_details.first_name && (
                            <div className="order-delivery-field">
                              <strong>Ім'я:</strong> {order.delivery_details.first_name}
                            </div>
                          )}
                          {order.delivery_details.last_name && (
                            <div className="order-delivery-field">
                              <strong>Прізвище:</strong> {order.delivery_details.last_name}
                            </div>
                          )}
                          {order.delivery_details.phone_number && (
                            <div className="order-delivery-field">
                              <strong>Телефон:</strong> {order.delivery_details.phone_number}
                            </div>
                          )}
                          {order.delivery_details.email && (
                            <div className="order-delivery-field">
                              <strong>Email:</strong> {order.delivery_details.email}
                            </div>
                          )}
                          {order.delivery_details.delivery_city && (
                            <div className="order-delivery-field">
                              <strong>Місто:</strong> {order.delivery_details.delivery_city}
                            </div>
                          )}
                          <div className="order-delivery-field">
                            <strong>Тип доставки:</strong> Нова Пошта
                          </div>
                          {order.delivery_details.delivery_type === 'department' && (
                            <div className="order-delivery-field">
                              <strong>Відділення:</strong> {order.delivery_details.delivery_department || 'Не вказано'}
                            </div>
                          )}
                          {order.delivery_details.delivery_type === 'postomat' && (
                            <div className="order-delivery-field">
                              <strong>Поштомат:</strong> {order.delivery_details.delivery_department || 'Не вказано'}
                            </div>
                          )}
                          {order.delivery_details.delivery_type === 'address' && order.delivery_details.delivery_address && (
                            <div className="order-delivery-field">
                              <strong>Адреса:</strong> {order.delivery_details.delivery_address}
                            </div>
                          )}
                          {order.delivery_details.delivery_time && (
                            <div className="order-delivery-field">
                              <strong>Час доставки:</strong> {order.delivery_details.delivery_time}
                            </div>
                          )}
                          {order.delivery_details.additional_details && (
                            <div className="order-delivery-field">
                              <strong>Додаткові деталі:</strong> {order.delivery_details.additional_details}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                {order.line_items && order.line_items.length > 0 && (
                  <div className="order-items">
                    {order.line_items.map((item) => (
                      <div 
                        key={item.id} 
                        className="order-item-line"
                        onClick={() => {
                          if (item.product_id) {
                            navigate(`/products/${item.product_id}?from=orders`);
                          }
                        }}
                      >
                        <div className="order-item-title-wrapper">
                          <span className="order-item-title">
                            {item.product_title} ({formatSizeSpaced(item.variation_size)})
                          </span>
                          <span className="order-item-quantity">×{item.quantity}</span>
                        </div>
                        <span className="order-item-price">
                          {item.price % 1 === 0 ? item.price.toString() : item.price.toFixed(2)} ₴
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="order-item-footer">
                  <div className="order-footer-top">
                    <div className="order-status-wrapper">
                      <div className="order-status">
                        <span className={`order-status-pill order-status-pill--${order.status || 'pending'}`}>
                          {order.status === 'cancelled' ? 'Скасовано' : order.status === 'pending' ? 'В обробці' : order.status === 'completed' ? 'Відправлено' : order.status || 'В обробці'}
                        </span>
                      </div>
                      {order.status === 'completed' && order.delivered_at && (
                        <div className="order-status-time">
                          {(() => {
                            const date = new Date(order.delivered_at);
                            const dateStr = date.toLocaleDateString('uk-UA');
                            const timeStr = date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
                            return `${dateStr} ${timeStr}`;
                          })()}
                        </div>
                      )}
                      {order.status === 'cancelled' && order.cancelled_at && (
                        <div className="order-status-time">
                          {(() => {
                            const date = new Date(order.cancelled_at);
                            const dateStr = date.toLocaleDateString('uk-UA');
                            const timeStr = date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
                            return `${dateStr} ${timeStr}`;
                          })()}
                        </div>
                      )}
                    </div>
                    {order.total && (
                      <div className="order-total">
                        Сума: {order.total % 1 === 0 ? order.total.toString() : order.total.toFixed(2)} ₴
                      </div>
                    )}
                  </div>
                  {order.status === 'pending' && order.is_buyer && (
                    <div className="order-footer-bottom">
                      <button 
                        className="order-cancel-btn"
                        onClick={() => handleCancelClick(order)}
                      >
                        Скасувати
                      </button>
                    </div>
                  )}
                  {order.status === 'pending' && order.is_seller && (
                    <div className="order-footer-bottom">
                      <button 
                        className="order-cancel-btn"
                        onClick={() => handleCancelOrderAsSeller(order)}
                      >
                        Скасувати
                      </button>
                      {order.buy_account_id !== order.seller_account_id && !(order.is_buyer && order.is_seller) && (
                      <button 
                        className="order-complete-btn"
                        onClick={() => handleCompleteClick(order)}
                      >
                        Відправлено
                      </button>
                      )}
                    </div>
                  )}
                  {order.is_buyer && (
                    <div className="order-footer-bottom">
                      <button 
                        className="order-reorder-btn"
                        onClick={() => handleReorder(order)}
                      >
                        Повторити замовлення
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showCancelPopup && (
        <ConfirmPopup
          message="Скасувати це замовлення?"
          onConfirm={confirmCancellation}
          onCancel={cancelCancellation}
        />
      )}
      {showCompletePopup && (
        <ConfirmPopup
          message="Ви підтверджуєте, що замовлення відправлено?"
          onConfirm={confirmCompletion}
          onCancel={cancelCompletion}
        />
      )}
    </div>
  );
}

